import { SubmitHandler, useForm } from 'react-hook-form';
import { Button, HStack, Input, Spinner, Text, VStack } from 'ui_2';
import { zodCreateSponsorInput } from 'jigsaw';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpcReact } from '@src/data/trpc';
import z from 'zod';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { ImagePreview } from './ImagePreview';

type FormInput = z.infer<typeof zodCreateSponsorInput>;

export const CreateSponsor = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isLoading, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(zodCreateSponsorInput),
  });

  const watchedValues = watch();

  const { error, mutateAsync } = trpcReact.createSponsor.useMutation({
    onSuccess: () => {
      toast.success(`Created new Sponsor: ${watchedValues.name}`);
    },
  });

  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    await mutateAsync(data);
  };

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  return (
    <VStack className='mx-auto w-full space-y-6'>
      <form onSubmit={handleSubmit(onSubmit)} className='w-full space-y-4'>
        {/* Basic Info */}
        <Input
          {...register('name')}
          label='Sponsor Name'
          placeholder='Aleo'
          error={errors.name?.message?.toString()}
        />
        <Input
          {...register('url')}
          label='Sponsor URL'
          placeholder='https://aleo.org'
          error={errors.url?.message?.toString()}
        />

        {/* Logo */}
        <Input
          {...register('logoURL')}
          type='url'
          label='Logo URL'
          placeholder='https://img.com/1234'
          error={errors.logoURL?.message?.toString()}
          rightElement={
            watchedValues.logoURL ? (
              <ImagePreview url={watchedValues.logoURL} label='Logo' />
            ) : undefined
          }
        />

        {/* Background Images */}
        <div className='flex flex-col space-y-4 md:flex-row md:gap-4 md:space-y-0'>
          <Input
            {...register('splashPageBackgroundImageMobileURL')}
            type='url'
            label='Mobile Background URL'
            placeholder='https://img.com/1234'
            error={errors.splashPageBackgroundImageMobileURL?.message?.toString()}
            rightElement={
              watchedValues.splashPageBackgroundImageMobileURL ? (
                <ImagePreview
                  url={watchedValues.splashPageBackgroundImageMobileURL}
                  label='Mobile Preview'
                />
              ) : undefined
            }
          />

          <Input
            {...register('splashPageBackgroundImageExtensionURL')}
            type='url'
            label='Desktop Background URL'
            placeholder='https://img.com/1234'
            error={errors.splashPageBackgroundImageExtensionURL?.message?.toString()}
            rightElement={
              watchedValues.splashPageBackgroundImageExtensionURL ? (
                <ImagePreview
                  url={watchedValues.splashPageBackgroundImageExtensionURL}
                  label='Desktop Preview'
                />
              ) : undefined
            }
          />
        </div>

        {/* Password at bottom */}
        <Input
          {...register('password')}
          type='password'
          label='Password'
          placeholder='Enter password'
          error={errors.password?.message?.toString()}
        />

        <Button
          type='submit'
          disabled={isLoading || isSubmitting || !isValid}
          className='w-full'
          variant='primary'
        >
          {isSubmitting ? (
            <HStack className='items-center gap-2'>
              <Text>Creating...</Text>
              <Spinner />
            </HStack>
          ) : (
            'Create Sponsor'
          )}
        </Button>

        {errors.root?.message && (
          <Text color='red' className='text-center'>
            {errors.root.message}
          </Text>
        )}
      </form>
    </VStack>
  );
};
